import { ActionTree } from 'vuex';
import { AxiosInstance, AxiosResponse } from 'axios';
import { handleAllErrors, toErrorMessage } from '@/utils/errorHandling';
import { ACTIVE_STATUSES } from '@/utils/bulkJob';
import { state as authState } from '../auth/auth';
import { BulkJobState, RootState } from '../types';
import Toast from '@/utils/toast';
import i18n from '@/i18n';

const POLL_INTERVAL_MS: number = 10_000;

/**
 * Build the action tree for a bulk-job module. Both async job features (enterprise menu pull,
 * menu replication) share an identical job/location contract; they differ only in their REST base
 * path and the default history sort column, so the implementation lives here once and is
 * parameterized per feature. A job is kicked off by POSTing to `basePath`; the job collection
 * (list, detail, cancel) lives under `basePath/jobs`.
 *
 * @param {string} basePath - REST base path for this feature (e.g. '/menus/replication')
 * @param {TableFilterParams} defaultFilters - default history page/limit/sort for the initial fetch
 * @return {ActionTree<BulkJobState, RootState>}
 */
export function createBulkJobActions(basePath: string, defaultFilters: TableFilterParams): ActionTree<BulkJobState, RootState> {
	const jobsPath: string = `${basePath}/jobs`;
	return {
		/**
		 * Start a new bulk job. Posts the payload, then loads the created job's detail and kicks off
		 * polling. Surfaces backend refusals (e.g. concurrent-job guard) via the shared error handler.
		 *
		 * @param {object} payload - feature-specific request body
		 * @return {Promise<void>}
		 */
		async startJob({ commit, dispatch }, payload: object): Promise<void> {
			const axiosInst: AxiosInstance = authState.axiosInst;
			try {
				const { data }: AxiosResponse<{ jobId: string }> = await axiosInst.post(basePath, payload);
				// A previous job's result is stale once a new job exists, and leaving it in place would
				// block the new one from auto-opening when it completes.
				commit('SET_VIEWED_JOB', null);
				await dispatch('fetchJobStatus', data.jobId);
				dispatch('startPolling');
			}
			catch (error) {
				handleAllErrors(error);
			}
		},

		/**
		 * Called on page mount. Fetches page 1 of history to populate the table, finds any
		 * currently-active job for the user, and resumes polling for it if present.
		 *
		 * @return {Promise<void>}
		 */
		async fetchActiveJob({ commit, dispatch }): Promise<void> {
			const axiosInst: AxiosInstance = authState.axiosInst;
			try {
				// Use defaults for the initial fetch so the active job (most recent) lands on page 1.
				// The component controls ongoing pagination via fetchJobHistory.
				const params: TableFilterParams = { ...defaultFilters };
				const { data }: AxiosResponse<PaginatedResults<BulkJob>> = await axiosInst.get(jobsPath, { params });
				const active: BulkJob | undefined = data.rows.find((job: BulkJob) => ACTIVE_STATUSES.includes(job.status));
				commit('SET_HISTORY', data.rows);
				commit('SET_HISTORY_COUNT', data.count);
				commit('SET_HISTORY_FILTERS', params);
				if (active) {
					await dispatch('fetchJobStatus', active.jobId);
					dispatch('startPolling');
				}
				else {
					commit('CLEAR_ACTIVE_JOB');
				}
			}
			catch (error) {
				handleAllErrors(error);
			}
		},

		/**
		 * Fetch full detail for a specific job and route the result:
		 * - Active status: update `activeJob` so the progress card re-renders.
		 * - Terminal status: auto-open the completed job as `viewedJob` (if nothing else is being viewed),
		 *   stop polling, and refresh history using the user's current filters.
		 * Stops polling on error to avoid hammering a broken endpoint every 10s.
		 *
		 * @param {string} jobId
		 * @return {Promise<void>}
		 */
		async fetchJobStatus({ commit, dispatch, state }, jobId: string): Promise<void> {
			const axiosInst: AxiosInstance = authState.axiosInst;
			try {
				const { data }: AxiosResponse<BulkJob> = await axiosInst.get(`${jobsPath}/${jobId}`);
				if (ACTIVE_STATUSES.includes(data.status)) {
					commit('SET_ACTIVE_JOB', data);
				}
				else {
					// Auto-view the just-completed job so the user lands on its result
					// instead of the start form. Only if they aren't already viewing a different historical job.
					if (!state.viewedJob) {
						commit('SET_VIEWED_JOB', data);
					}
					commit('CLEAR_ACTIVE_JOB');
					dispatch('stopPolling');
					// Replay the user's current history filters so we don't silently wipe their pagination/sort.
					await dispatch('fetchJobHistory', state.jobHistoryFilters);
				}
			}
			catch (error) {
				// Stop polling on error before re-throwing so we don't hammer a broken endpoint every 10s.
				if (state.pollIntervalId) {
					dispatch('stopPolling');
				}
				handleAllErrors(error);
			}
		},

		/**
		 * Fetch a paginated/sorted slice of the user's job history and persist the filters used,
		 * so subsequent auto-refreshes (e.g. post-cancel) replay the same view instead of resetting.
		 * Falls back to the last-persisted filters when called with no args.
		 *
		 * @param {TableFilterParams} [params] - page / limit / sortBy / sortDirection; optional
		 * @return {Promise<void>}
		 */
		async fetchJobHistory({ commit, state }, params?: TableFilterParams): Promise<void> {
			const axiosInst: AxiosInstance = authState.axiosInst;
			const effectiveParams: TableFilterParams = params || state.jobHistoryFilters;
			try {
				const { data }: AxiosResponse<PaginatedResults<BulkJob>> = await axiosInst.get(jobsPath, { params: effectiveParams });
				commit('SET_HISTORY', data.rows);
				commit('SET_HISTORY_COUNT', data.count);
				commit('SET_HISTORY_FILTERS', effectiveParams);
			}
			catch (error) {
				handleAllErrors(error);
			}
		},

		/**
		 * Load a historical job's full detail into `viewedJob`, used when a user clicks a row
		 * in the history table. Does not affect `activeJob` or polling.
		 *
		 * @param {string} jobId
		 * @return {Promise<void>}
		 */
		async fetchViewedJob({ commit }, jobId: string): Promise<void> {
			const axiosInst: AxiosInstance = authState.axiosInst;
			try {
				const { data }: AxiosResponse<BulkJob> = await axiosInst.get(`${jobsPath}/${jobId}`);
				commit('SET_VIEWED_JOB', data);
			}
			catch (error) {
				handleAllErrors(error);
			}
		},

		/**
		 * Clear the currently-viewed historical job so the user returns to the start form
		 * (or active job view, if one exists). Used by the "Back" button and beforeDestroy.
		 *
		 * @return {void}
		 */
		clearViewedJob({ commit }): void {
			commit('SET_VIEWED_JOB', null);
		},

		/**
		 * Cancel an in-flight job. Immediately refreshes both the job's status and the history
		 * (using the current filters) so the UI reflects the cancelled state without waiting for the next poll.
		 *
		 * @param {string} jobId
		 * @return {Promise<void>}
		 */
		async cancelJob({ dispatch, state }, jobId: string): Promise<void> {
			const axiosInst: AxiosInstance = authState.axiosInst;
			try {
				await axiosInst.post(`${jobsPath}/${jobId}/cancel`);
				await dispatch('fetchJobStatus', jobId);
				await dispatch('fetchJobHistory', state.jobHistoryFilters);
			}
			catch (error) {
				handleAllErrors(error);
			}
		},

		/**
		 * Begin polling `fetchJobStatus` for the current active job every POLL_INTERVAL_MS.
		 * No-op if polling is already running or no active job is set. On poll failure,
		 * `fetchJobStatus` stops polling internally and this handler surfaces a toast to the user.
		 *
		 * @return {void}
		 */
		startPolling({ commit, dispatch, state }): void {
			if (state.pollIntervalId || !state.activeJob) {
				return;
			}
			const jobId: string = state.activeJob.jobId;
			const intervalId: number = window.setInterval(() => {
				dispatch('fetchJobStatus', jobId).catch((error: unknown) => {
					// fetchJobStatus already stopped polling before re-throwing. Surface a toast once
					// so the user knows progress updates have halted and they can refresh.
					Toast.error(i18n.t('shared.bulk_job.polling_stopped', { error: toErrorMessage(error) }));
				});
			}, POLL_INTERVAL_MS);
			commit('SET_POLL_INTERVAL_ID', intervalId);
		},

		/**
		 * Clear the polling interval and reset the stored interval id. Safe to call multiple times.
		 *
		 * @return {void}
		 */
		stopPolling({ commit, state }): void {
			if (state.pollIntervalId) {
				window.clearInterval(state.pollIntervalId);
				commit('SET_POLL_INTERVAL_ID', null);
			}
		}
	};
}
